import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import InfoIcon from '@mui/icons-material/Info';

// Mock data for analytics
const analyticsData = [
  { date: 'Mon', impressions: 1200, clicks: 240, ctr: 20 },
  { date: 'Tue', impressions: 1500, clicks: 300, ctr: 20 },
  { date: 'Wed', impressions: 1800, clicks: 360, ctr: 20 },
  { date: 'Thu', impressions: 1600, clicks: 320, ctr: 20 },
  { date: 'Fri', impressions: 2000, clicks: 400, ctr: 20 },
  { date: 'Sat', impressions: 2200, clicks: 440, ctr: 20 },
  { date: 'Sun', impressions: 1900, clicks: 380, ctr: 20 },
];

interface AdAnalyticsProps {
  role: 'advertiser' | 'publisher';
}

const AdAnalytics: React.FC<AdAnalyticsProps> = ({ role }) => {
  const totalImpressions = analyticsData.reduce((sum, day) => sum + day.impressions, 0);
  const totalClicks = analyticsData.reduce((sum, day) => sum + day.clicks, 0);
  const averageCTR = (totalClicks / totalImpressions * 100).toFixed(1);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
        Performance Analytics
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, backgroundColor: '#1E293B' }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94A3B8" />
                <YAxis stroke="#94A3B8" />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    color: '#E2E8F0'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#3B82F6" 
                  name="Impressions"
                />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#10B981" 
                  name="Clicks"
                />
                <Line 
                  type="monotone" 
                  dataKey="ctr" 
                  stroke="#F59E0B" 
                  name="CTR (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#1E293B' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Total Impressions
                </Typography>
                <Tooltip title="Total number of times your ad was shown">
                  <IconButton size="small" sx={{ color: '#94A3B8' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h4" sx={{ color: '#3B82F6' }}>
                {totalImpressions.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#1E293B' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Total Clicks
                </Typography>
                <Tooltip title="Total number of times your ad was clicked">
                  <IconButton size="small" sx={{ color: '#94A3B8' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h4" sx={{ color: '#10B981' }}>
                {totalClicks.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#1E293B' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Average CTR
                </Typography>
                <Tooltip title="Click-through rate (clicks divided by impressions)">
                  <IconButton size="small" sx={{ color: '#94A3B8' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h4" sx={{ color: '#F59E0B' }}>
                {averageCTR}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdAnalytics; 